import Analytics from '@/utils/analytics';
import React, { useState } from 'react';
import {
  ACTION_SHEET_MAX_HEIGHT,
  BORDER_RADIUS_SMALL,
  LIST_MARGIN_HORIZONTAL,
  TYPOGRAPHY,
} from '@/styles';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { createStyles } from '@/contexts/theme-context';
import { EqualHeightTouchableOpacity } from '@/components/equal-height-touchable-opacity';
import { ItemDetailContextProvider } from '@/contexts/item-detail-context';
import { Platform, StyleProp, ViewStyle } from 'react-native';
import { SCROLL_DELAY } from '@/components/list-item';
import { useLayoutContext } from '@/contexts/layout-context';
import { VolumePromotion } from '@/utils/volume-promotions-types';
import { VolumePromotionBanner } from './volume-promotion-banner';
import { VolumePromotionDetailComponent } from './volume-promotion-detail-component';

export const VOLUME_PROMOTION_CARD_HEIGHT = 280;

interface Props {
  onNavigateToDetails: (promotionUUID: string) => void;
  promotion: VolumePromotion;
  style?: StyleProp<ViewStyle>;
}

export const VolumePromotionListItem: React.FC<Props> = ({
  onNavigateToDetails,
  promotion,
  style,
}) => {
  const { isSmallLayout } = useLayoutContext();
  const [styles] = useStyles();

  const [showDetails, setShowDetails] = useState(false);

  const hasPromotedVariantPacks = Boolean(
    promotion.promotedVariantPacks.length,
  );

  return (
    <>
      <EqualHeightTouchableOpacity
        delayPressIn={SCROLL_DELAY}
        disabled={!hasPromotedVariantPacks}
        onPress={() => {
          const promotionUUID = promotion.uuid;
          if (isSmallLayout) {
            onNavigateToDetails(promotionUUID);
          } else {
            setShowDetails(true);
          }

          Analytics.track('promotion_list.promotion.press', {
            promotionUUID,
          });
        }}
        style={[styles.base, style, styles.fixedHeight]}
      >
        <VolumePromotionBanner
          marketingTextStyle={[TYPOGRAPHY.h3, styles.marketingText]}
          promotion={promotion}
          style={[styles.banner, styles.fixedHeight]}
        />
      </EqualHeightTouchableOpacity>
      {showDetails ? (
        <BaseActionSheet
          contentStyle={styles.promotionDetailsSheetContent}
          isVisible
          modalStyle={styles.promotionDetailsSheetModal}
          onClose={() => setShowDetails(false)}
          title={promotion.hideTagline ? undefined : promotion.tagline}
        >
          {/* Hacky fix (seemingly Modal-inside-Modal issue, but not certain)
            - the ItemDetailContextProvider fixes an iPad bug where double modals causes app crash https://github.com/usepepper/restaurant-app/pull/4934
            - the same fix causes image carousel content to bleed on web, so disable
          */}
          {Platform.OS === 'web' ? (
            <VolumePromotionDetailComponent
              listContainerStyle={styles.promotionDetailsListContainer}
              promotion={promotion}
            />
          ) : (
            <ItemDetailContextProvider>
              <VolumePromotionDetailComponent
                listContainerStyle={styles.promotionDetailsListContainer}
                promotion={promotion}
              />
            </ItemDetailContextProvider>
          )}
        </BaseActionSheet>
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  banner: {
    borderColor: theme.sk.color.border.primary,
    borderRadius: BORDER_RADIUS_SMALL,
    borderWidth: 1,
    flex: -1,
    overflow: 'hidden',
  },
  base: {
    paddingBottom: 8,
  },
  fixedHeight: {
    height: VOLUME_PROMOTION_CARD_HEIGHT,
  },
  marketingText: {
    fontSize: 21,
    lineHeight: 25,
  },
  promotionDetailsListContainer: {
    marginHorizontal: -LIST_MARGIN_HORIZONTAL,
  },
  promotionDetailsSheetContent: {
    flex: 1,
  },
  promotionDetailsSheetModal: {
    flex: 1,
    maxHeight: ACTION_SHEET_MAX_HEIGHT,
  },
}));
